import React from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Avatar, GalleryDiorama } from '@/design-system'
import { avatarGradient } from '@/features/connections/utils'
import { ReportMenu } from '@/features/album/components/ReportMenu'
import { DiscoverFeedItem } from '../../hooks/useDiscoverFeed'
import { resolveGalleryTint } from '../../utils/galleryTint'
import { CoverMesh } from '../CoverMesh'

export interface DiscoverCardProps {
  item: DiscoverFeedItem
  index?: number
  onClick?: () => void
  onReport?: () => void
}

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`

const Root = styled(Box)({
  position: 'relative',
  borderRadius: 17,
  overflow: 'hidden',
  background: 'var(--ds-canvas)',
  boxShadow: 'var(--ds-shadow-soft)',
  cursor: 'pointer',
  animation: `${fadeUp} 480ms var(--ds-ease-out) both`,
  transition: `transform var(--ds-duration-normal) var(--ds-ease-out), box-shadow var(--ds-duration-normal) var(--ds-ease)`,
  '&:hover': { transform: 'translateY(-4px)', boxShadow: 'var(--ds-shadow-lift)' },
  '&:hover .disc-edge': { opacity: 1 },
  '&:hover .disc-cta': { opacity: 1, transform: 'translateY(0)' },
  '&:hover .disc-img, &:hover .disc-dio': { transform: 'scale(1.045)' },
  '&:hover .card-kebab': { opacity: 1 },
})

const Edge = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 2.5,
  zIndex: 5,
  background: 'var(--ds-gradient-spectrum)',
  opacity: 0,
  transition: `opacity var(--ds-duration-normal) var(--ds-ease)`,
})

const Cov = styled(Box)({
  position: 'relative',
  aspectRatio: '4 / 3',
  overflow: 'hidden',
  background: 'var(--ds-surface)',
})

const CovImg = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  transition: `transform var(--ds-duration-slow) var(--ds-ease)`,
})

const DioWrap = styled(Box)({ width: '100%', height: '100%', transition: `transform var(--ds-duration-slow) var(--ds-ease)` })

const Veil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, transparent 62%, rgba(0,0,0,0.28))',
  zIndex: 1,
  pointerEvents: 'none',
})

const KindTag = styled(Box, { shouldForwardProp: (p) => p !== '$ink' })<{ $ink: string }>(({ $ink }) => ({
  position: 'absolute',
  top: 11,
  left: 11,
  zIndex: 3,
  height: 23,
  padding: '0 10px',
  borderRadius: 'var(--ds-radius-pill)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  background: 'rgba(255,255,255,0.9)',
  color: $ink,
  boxShadow: 'inset 0 0 0 1px rgba(13,13,15,0.06)',
  backdropFilter: 'blur(6px)',
}))

const WalkChip = styled(Box)({
  position: 'absolute',
  top: 11,
  right: 11,
  zIndex: 3,
  height: 23,
  padding: '0 9px',
  borderRadius: 'var(--ds-radius-pill)',
  background: 'rgba(13,13,15,0.6)',
  backdropFilter: 'blur(6px)',
  color: 'var(--ds-alert-warn)',
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 8.5,
  letterSpacing: '0.1em',
  display: 'flex',
  alignItems: 'center',
})

const OpenCta = styled(Box)({
  position: 'absolute',
  right: 11,
  bottom: 11,
  zIndex: 3,
  height: 30,
  padding: '0 14px',
  borderRadius: 'var(--ds-radius-pill)',
  background: '#fff',
  color: 'var(--ds-ink)',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  boxShadow: 'var(--ds-shadow-lift)',
  opacity: 0,
  transform: 'translateY(8px)',
  transition: `all var(--ds-duration-normal) var(--ds-ease-out)`,
})

const Meta = styled(Box)({ padding: '13px 15px 14px' })

const Title = styled(Typography)({
  fontSize: 14.5,
  fontWeight: 680,
  letterSpacing: '-0.015em',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: 'var(--ds-ink)',
})

const Row2 = styled(Box)({ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, minWidth: 0 })

const OwnerName = styled(Typography)({
  fontSize: 12,
  color: 'var(--ds-ink-muted)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
})

const Likes = styled(Box)({
  marginLeft: 'auto',
  flexShrink: 0,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 10,
  color: 'var(--ds-ink-subtle)',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
})

/**
 * "Fresh on the wall" grid card — uniform 4:3 cover (photo or room
 * diorama), a tinted content-kind tag carrying the same color system as
 * `CategoryTiles`, a walkable chip when applicable, a spectrum hairline +
 * slide-up "Open"/"Step inside" CTA on hover, owner + likes below the title.
 */
export function DiscoverCard({ item, index = 0, onClick, onReport }: DiscoverCardProps) {
  const summary = item.kind === 'album' ? item.album : item.gallery
  if (!summary) return null

  const owner = summary.owner
  const isGallery = item.kind === 'gallery' && Boolean(item.gallery)
  const isWalkable = isGallery || Boolean(item.album?.galleryId)

  return (
    <Root
      role='button'
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === 'Enter' && onClick?.()}
      style={{ animationDelay: `${Math.min(index, 14) * 30}ms` }}
    >
      <Edge className='disc-edge' />
      <Cov>
        {isGallery && item.gallery ? (
          <DioWrap className='disc-dio'>
            <GalleryDiorama
              wallColor={resolveGalleryTint(item.gallery).wallColor}
              floorColor={resolveGalleryTint(item.gallery).floorColor}
              arts={item.gallery.arts}
              light={resolveGalleryTint(item.gallery).light}
            />
          </DioWrap>
        ) : item.album?.coverUrl ? (
          <CovImg className='disc-img' src={item.album.coverUrl} alt={item.album.title} loading='lazy' />
        ) : (
          <CoverMesh />
        )}
        <Veil />
        <KindTag $ink={isGallery ? 'var(--ds-alert-ok-ink)' : 'var(--ds-alert-warn-ink)'}>
          {isGallery ? '3D GALLERY' : 'ALBUM'}
        </KindTag>
        {isWalkable && <WalkChip>&#9672; WALKABLE</WalkChip>}
        <OpenCta className='disc-cta'>
          {isGallery ? 'Step inside' : 'Open'} &rarr;
        </OpenCta>
        {onReport && <ReportMenu itemLabel={summary.title} onReport={onReport} />}
      </Cov>
      <Meta>
        <Title>{summary.title}</Title>
        {owner && (
          <Row2>
            <Avatar sx={{ width: 20, height: 20, fontSize: 9, background: avatarGradient(owner.name), color: '#fff' }}>
              {owner.name?.[0]?.toUpperCase()}
            </Avatar>
            <OwnerName>{owner.name}</OwnerName>
            <Likes>
              <span style={{ color: 'var(--ds-accent-dot-2)' }}>&#9829;</span> {item.likes}
            </Likes>
          </Row2>
        )}
      </Meta>
    </Root>
  )
}
